import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Checkbox,
  Box,
  Typography,
  Divider,
  Radio,
  RadioGroup,
  FormControlLabel
} from "@mui/material";
import { Settings as SettingsIcon } from "@mui/icons-material";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

export interface TabConfig {
  id: string;
  label: string;
  visible: boolean;
  isDefault: boolean;
  view: 'ALL' | 'TODO' | 'IN_PROGRESS' | 'COMPLETED' | 'OVERDUE' | 'ASSIGNED_TO_ME';
}

interface TabConfigDialogProps {
  open: boolean;
  onClose: () => void;
  tabs: TabConfig[];
  onSave: (tabs: TabConfig[]) => void;
  title?: string;
}

const TabConfigDialog: React.FC<TabConfigDialogProps> = ({
  open,
  onClose,
  tabs,
  onSave,
  title = "Konfiguracja zakładek"
}) => {
  const [localTabs, setLocalTabs] = useState<TabConfig[]>([]);

  useEffect(() => {
    if (open) {
      setLocalTabs([...tabs]);
    }
  }, [open, tabs]);

  const handleDragEnd = (result: any) => {
    if (!result.destination) return;
    if (result.destination.index === result.source.index) return;

    const items = Array.from(localTabs);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setLocalTabs(items);
  };

  const handleVisibilityToggle = (tabId: string) => {
    setLocalTabs(prev => {
      const updated = prev.map(tab =>
        tab.id === tabId ? { ...tab, visible: !tab.visible } : tab
      );
      
      // If we're hiding the default tab, set the first visible tab as default
      const hiddenTab = updated.find(tab => tab.id === tabId);
      if (hiddenTab && !hiddenTab.visible && hiddenTab.isDefault) {
        const firstVisibleTab = updated.find(tab => tab.visible);
        if (firstVisibleTab) {
          return updated.map(tab => ({
            ...tab,
            isDefault: tab.id === firstVisibleTab.id
          }));
        }
      }
      
      return updated;
    });
  };

  const handleDefaultTabChange = (tabId: string) => {
    setLocalTabs(prev =>
      prev.map(tab => ({
        ...tab,
        isDefault: tab.id === tabId
      }))
    );
  };

  const handleSave = () => {
    // Ensure at least one tab is visible
    const visibleTabs = localTabs.filter(tab => tab.visible);
    if (visibleTabs.length === 0) {
      // Make the first tab visible and default
      const updatedTabs = localTabs.map((tab, index) => ({
        ...tab,
        visible: index === 0,
        isDefault: index === 0
      }));
      onSave(updatedTabs);
    } else {
      // Ensure exactly one tab is marked as default
      const defaultTab = localTabs.find(tab => tab.isDefault && tab.visible);
      if (!defaultTab) {
        const firstVisibleTab = visibleTabs[0];
        const updatedTabs = localTabs.map(tab => ({
          ...tab,
          isDefault: tab.id === firstVisibleTab.id
        }));
        onSave(updatedTabs);
      } else {
        onSave(localTabs);
      }
    }
    onClose();
  };

  const handleReset = () => {
    const resetTabs = tabs.map((tab, index) => ({ 
      ...tab, 
      visible: true,
      isDefault: index === 0 // First tab is default
    }));
    setLocalTabs(resetTabs);
  };

  const visibleTabs = localTabs.filter(tab => tab.visible);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <SettingsIcon color="primary" />
          {title}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Przeciągnij cały prostokąt, aby zmienić kolejność zakładek. Przełącz widoczność klikając checkbox. 
            Wybierz domyślną zakładkę używając kropeczkę w kółku.
          </Typography>
        </Box>
        <Divider sx={{ mb: 2 }} />
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="tab-config">
            {(provided) => (
              <div
                ref={provided.innerRef}
                {...provided.droppableProps}
                style={{
                  minHeight: 200,
                  display: "flex",
                  flexDirection: "column",
                  gap: 8
                }}
              >
                {localTabs.map((tab, index) => (
                  <Draggable
                    key={tab.id}
                    draggableId={tab.id}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <Box
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          px: 1,
                          py: 0,
                          borderRadius: 1,
                          background: snapshot.isDragging
                            ? "rgba(25, 118, 210, 0.13)"
                            : "#f5f8fa",
                          boxShadow: snapshot.isDragging
                            ? "0 2px 12px rgba(0,0,0,0.14)"
                            : "none",
                          transition: "background 0.2s, box-shadow 0.2s",
                          cursor: "grab",
                          userSelect: "none"
                        }}
                      >
                        <Typography
                          sx={{
                            flex: 1,
                            fontSize: "0.7rem",
                            fontWeight: 400,
                            color: "text.primary"
                          }}
                        >
                          {tab.label}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <RadioGroup
                            value={localTabs.find(t => t.isDefault)?.id || ''}
                            onChange={(e) => handleDefaultTabChange(e.target.value)}
                            sx={{ margin: 0 }}
                          >
                            <FormControlLabel
                              value={tab.id}
                              control={<Radio size="small" />}
                              label=""
                              disabled={!tab.visible}
                              sx={{ 
                                margin: 0,
                                '& .MuiFormControlLabel-label': { display: 'none' }
                              }}
                            />
                          </RadioGroup>
                          <Checkbox
                            checked={tab.visible}
                            onChange={() => handleVisibilityToggle(tab.id)}
                            color="primary"
                            size="small"
                          />
                        </Box>
                      </Box>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        {visibleTabs.length === 0 && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
            <Typography variant="body2" color="warning.dark">
              Uwaga: Przynajmniej jedna zakładka musi być widoczna.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleReset} variant="outlined" color="secondary">
          Resetuj
        </Button>
        <Button onClick={onClose} variant="outlined">
          Anuluj
        </Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Zapisz
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TabConfigDialog;